/**
 * 予約データをエディタパネル用に変換するユーティリティ
 * GridCalendar, ReservationList, ReservationTable で共通使用
 */

import { getCodeById } from './statusMapper';

/**
 * 予約オブジェクトからエディタ用オブジェクトを生成
 * @param reservation - 予約データ(APIレスポンスまたはオーバーレイ)
 * @param overlay - オーバーレイデータ(オプション)
 * @returns エディタ用の予約オブジェクト
 */
export const toEditorReservation = (reservation: Record<string, any> | null | undefined, overlay: Record<string, any> | null = null): Record<string, any> | null => {
    if (!reservation && !overlay) return null;

    const res: Record<string, any> = reservation ?? {};
    const ovl: Record<string, any> = overlay ?? {};
    const detail: Record<string, any> = res.detail ?? ovl.detail ?? {};

    // テキスト値をピック（reservation優先）
    const pickText = (key: string, fallback: string = ''): string => {
        const candidates = [res[key], ovl[key], detail[key]];
        for (const val of candidates) {
            if (val != null) {
                const sVal = String(val).trim();
                if (sVal.length > 0) return sVal;
            }
        }
        return fallback;
    };

    // 数値をピック
    const pickNumber = (key: string, fallback: number | null = 0): number | null => {
        const candidates = [res[key], ovl[key], detail[key]];
        for (const val of candidates) {
            if (val != null) {
                const num = Number(val);
                if (Number.isFinite(num)) return num;
            }
        }
        return fallback;
    };

    // ブール値をピック
    const toBool = (val: unknown): boolean => {
        if (val === '0' || val === 0 || val === 'false' || val === false || val === '') return false;
        return Boolean(val);
    };

    const pickBool = (key: string, fallback: boolean = false): boolean => {
        if (res[key] != null) return toBool(res[key]);
        if (ovl[key] != null) return toBool(ovl[key]);
        if (detail[key] != null) return toBool(detail[key]);
        return fallback;
    };

    // IDを解決
    const id = res.id ?? ovl.id ?? '';

    // ステータスコードまたはIDを解決
    const statusCode = ((): string | null => {
        const s = res.status ?? ovl.status ?? res.status_code ?? ovl.status_code;
        if (s != null) {
            // 文字列（コード）の場合
            if (typeof s === 'string' && !/^\d+$/.test(s) && s.length > 0) return s;

            // 数値または数値文字列（ID）の場合
            const num = Number(s);
            if (Number.isFinite(num) && num > 0) {
                const code = getCodeById(num);
                if (code) return code;
            }
        }

        // status_id の確認
        const sid = res.status_id ?? res.statusId ?? ovl.status_id ?? ovl.statusId;
        if (sid != null) {
            const num = Number(sid);
            if (Number.isFinite(num) && num > 0) {
                const code = getCodeById(num);
                if (code) return code;
            }
        }

        // 既存の予約でステータスが見つからない場合は、元のステータスを維持しようと試みる
        if (res.status || ovl.status) return res.status || ovl.status;

        return null;
    })();

    // リソースIDを解決
    const resourceId = pickText('resourceId', pickText('resource_id', pickText('vehicle_id', pickText('vehicleId', ''))));
    const vehicleId = resourceId;

    return {
        id: String(id),
        contact_code: pickText('contact_code'),
        contact_id: pickText('contact_id'),
        page_id: pickText('page_id', String(id)),
        customer_name: pickText('customer_name'),
        customer_email: pickText('customer_email'),
        customer_tel: pickText('customer_tel'),
        customer_address: pickText('customer_address'),
        start_date: pickText('start_date'),
        end_date: pickText('end_date'),
        resourceId: resourceId,
        vehicle_id: vehicleId,
        resource_id: vehicleId,
        vehicle_code: pickText('vehicle_code'),
        car_type: pickText('car_type', pickText('re_car_type', pickText('req_car_type'))),
        status: statusCode,
        arrival_flight: pickText('arrival_flight'),
        departure_flight: pickText('departure_flight'),
        contact_plan: pickText('contact_plan'),
        contact_media: pickText('contact_media'),
        pax: pickNumber('pax', 0),
        pax_children: pickNumber('pax_children', 0),
        child_seat: pickNumber('child_seat', 0),
        booster_seat: pickNumber('booster_seat', 0),
        junior_seat: pickNumber('junior_seat', 0),
        invoice_link: pickText('invoice_link'),
        comment: pickText('comment'),
        memo: pickText('memo'),
        option: pickText('option'),
        // 価格
        base_price: pickNumber('base_price', null),
        option_price: pickNumber('option_price', null),
        noc_price: pickNumber('noc_price', null),
        noc: pickBool('noc'),
        cdw_price: pickNumber('cdw_price', null),
        cdw: pickBool('cdw'),
        sum_price: pickNumber('sum_price', null),
        days: pickNumber('days', null),
        nights: pickNumber('nights', null),
        coupon: pickText('coupon'),
        point: pickNumber('point', null),
        // booking情報
        booking_date: pickText('booking_date'),
        past_reservation_count: pickNumber('past_reservation_count', 0),
        // 確認フラグ
        license: pickBool('license'),
        payment: pickBool('payment'),
        terms_and_conditions: pickBool('terms_and_conditions'),
        disclaimer: pickBool('disclaimer'),
        agreement: pickBool('agreement'),
        // メールフラグ
        mail_send_flg1: pickBool('mail_send_flg1'),
        mail_send_flg2: pickBool('mail_send_flg2'),
        mail_send_flg3: pickBool('mail_send_flg3'),
        // 連絡手段
        communication_tools: res.communication_tools || ovl.communication_tools || detail.communication_tools || [],
        communication_tools_display: pickText('communication_tools_display'),
        // 緊急連絡先
        emergency_name: pickText('emergency_name'),
        emergency_rel: pickText('emergency_rel'),
        emergency_tel: pickText('emergency_tel'),
        // 場所
        rental_location: pickText('rental_location'),
        return_location: pickText('return_location'),
        // スタッフID
        pu_staff_id: res.pu_staff_id ?? ovl.pu_staff_id ?? null,
        do_staff_id: res.do_staff_id ?? ovl.do_staff_id ?? null,
        rent_staff_id: res.rent_staff_id ?? ovl.rent_staff_id ?? null,
        return_staff_id: res.return_staff_id ?? ovl.return_staff_id ?? null,
        // その他新規フィールド
        short_memo: pickText('short_memo'),
        request_coming_time: pickText('request_coming_time'),
        request_returning_time: pickText('request_returning_time'),
        // 色
        bg_color: pickText('bg_color'),
        text_color: pickText('text_color'),
        // JSONデータ (v1.17.0)
        json_data: (() => {
            let jsonData: unknown = res.json_data || ovl.json_data || detail.json_data || null;
            if (typeof jsonData === 'string' && jsonData.trim()) {
                try {
                    return JSON.parse(jsonData);
                } catch (e) {
                    console.warn('editorDataTransform', 'failed to parse json_data', e);
                }
            }
            return jsonData;
        })(),
        // 新規予約フラグ
        __isNew: res.__isNew || ovl.__isNew || false,
        // コピーフラグ
        __isCopy: res.__isCopy || ovl.__isCopy || false,
    };
};

export default toEditorReservation;
